import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Save, UserCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function SeniorProfileEditor() {
  const { seniorId, householdId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isNewProfile, setIsNewProfile] = useState(!seniorId);
  
  const [formData, setFormData] = useState({
    name: '',
    image_url: '',
    language: 'de',
    religion: 'none',
    household_id: householdId || '',
    device_id: '',
    modules_enabled: {
      religion_module: false,
      reminders: true,
      emergency: true
    }
  });

  useEffect(() => {
    if (seniorId) {
      fetchSeniorProfile();
    }
  }, [seniorId]);

  const fetchSeniorProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/seniors/${seniorId}`);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching senior profile:', error);
      toast.error('Fehler beim Laden des Profils');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isNewProfile) {
        await axios.post(`${API}/seniors`, formData);
        toast.success('Senior-Profil erfolgreich erstellt!');
      } else {
        await axios.put(`${API}/seniors/${seniorId}`, formData);
        toast.success('Senior-Profil erfolgreich aktualisiert!');
      }
      navigate(-1);
    } catch (error) {
      console.error('Error saving senior profile:', error);
      toast.error('Fehler beim Speichern des Profils');
    } finally {
      setLoading(false);
    }
  };

  const handleModuleToggle = (moduleName, value) => {
    setFormData({
      ...formData,
      modules_enabled: {
        ...formData.modules_enabled,
        [moduleName]: value
      }
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCircle className="w-6 h-6" />
              {isNewProfile ? 'Neues Senior-Profil erstellen' : 'Senior-Profil bearbeiten'}
            </CardTitle>
            <CardDescription>
              Verwalten Sie die Einstellungen und Module für das Senior-Profil
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Grundinformationen</h3>
                
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="z.B. Oma Erika"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="image-url">Profilbild URL (optional)</Label>
                  <Input
                    id="image-url"
                    value={formData.image_url || ''}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="language">Sprache</Label>
                    <Select
                      value={formData.language}
                      onValueChange={(value) => setFormData({ ...formData, language: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="de">Deutsch</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="tr">Türkçe</SelectItem>
                        <SelectItem value="ar">العربية</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="religion">Religion</Label>
                    <Select
                      value={formData.religion}
                      onValueChange={(value) => {
                        setFormData({ ...formData, religion: value });
                        // Auto-enable religion module if religion is selected
                        if (value !== 'none') {
                          handleModuleToggle('religion_module', true);
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Keine</SelectItem>
                        <SelectItem value="islam">Islam</SelectItem>
                        <SelectItem value="christian">Christentum</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Module Settings */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Module</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Erinnerungen</p>
                      <p className="text-sm text-gray-500">Medikamente, Routinen und Termine</p>
                    </div>
                    <Switch
                      checked={formData.modules_enabled.reminders}
                      onCheckedChange={(checked) => handleModuleToggle('reminders', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Notfall-Funktion</p>
                      <p className="text-sm text-gray-500">Sprachaktivierte Notfall-Benachrichtigungen</p>
                    </div>
                    <Switch
                      checked={formData.modules_enabled.emergency}
                      onCheckedChange={(checked) => handleModuleToggle('emergency', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Religiöses Modul</p>
                      <p className="text-sm text-gray-500">
                        {formData.religion === 'islam' ? 'Gebetszeiten und religiöse Inhalte' : 
                         formData.religion === 'christian' ? 'Religiöse Inhalte' : 
                         'Wählen Sie zuerst eine Religion aus'}
                      </p>
                    </div>
                    <Switch
                      checked={formData.modules_enabled.religion_module}
                      onCheckedChange={(checked) => handleModuleToggle('religion_module', checked)}
                      disabled={formData.religion === 'none'}
                    />
                  </div>
                </div>
              </div>

              {/* Islamic Settings Link */}
              {formData.religion === 'islam' && !isNewProfile && (
                <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                  <p className="text-sm text-indigo-800 mb-2">
                    🕌 <strong>Islamische Einstellungen:</strong> Konfigurieren Sie Gebetszeiten, Quran-Verse und mehr
                  </p>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => navigate(`/seniors/${seniorId}/religion`)}
                    className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                  >
                    Zu den islamischen Einstellungen →
                  </Button>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex gap-2">
                <Button type="submit" disabled={loading}>
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Speichere...' : 'Speichern'}
                </Button>
                <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                  Abbrechen
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
